// Renders an assistant answer: prose streams through Streamdown while embedded
// `netwatch-table` blocks render as real React (shaped severity icons), and
// colour-only severity emoji / `sev:N` tokens become inline shaped icons.
//
// This is the canonical answer renderer shared by the widget and the dashboard
// (see chat-content.css for the styles the host must load — the widget ships
// them in its shadow-DOM CSS, the dashboard imports the stylesheet).

import { useMemo, useState } from 'react'
import type { ComponentProps, ReactNode } from 'react'
import { Streamdown } from 'streamdown'
import { NetwatchTable } from './NetwatchTable'
import type { TableData } from './NetwatchTable'
import { NetwatchChart } from './NetwatchChart'
import type { ChartData } from './NetwatchChart'
import type { ChartPayload } from './chartSpec'
import { FindingsBlock } from './FindingsBlock'
import { dropDuplicateTables, orphanFindingsCards, parseFindingsBlock, resolveFindingsRef } from './findingsSpec'
import type { FindingsBlock as FindingsData } from './findingsSpec'
import { SeverityIcon } from './Severity'
import { EMOJI_SEV } from './severity'

function parseNetwatchTable(raw: string): TableData | null {
  try {
    const data = JSON.parse(raw)
    if (data && Array.isArray(data.columns) && Array.isArray(data.rows)) return data
  } catch {
    // Incomplete JSON while streaming, or malformed — NetwatchTable falls back.
  }
  return null
}

// Shape-sniff only: a chart block is anything carrying data OR a ref. Deep
// validation belongs to normalizeChartBlock, so this stays tolerant of every
// schema version (legacy `points`, `series`, categorical `labels`, ref-only).
function parseNetwatchChart(raw: string): ChartData | null {
  try {
    const data = JSON.parse(raw)
    if (
      data && typeof data === 'object' &&
      (Array.isArray(data.points) || Array.isArray(data.series) ||
       Array.isArray(data.labels) || typeof data.ref === 'string')
    ) return data as ChartData
  } catch {
    // Incomplete JSON while streaming, or malformed — NetwatchChart falls back.
  }
  return null
}

// Find the end index (inclusive) of the balanced JSON object that starts at
// `start` (content[start] === '{'), respecting strings/escapes. Returns -1 if
// it never closes (e.g. mid-stream).
function matchBrace(content: string, start: number): number {
  let depth = 0
  let inStr = false
  for (let i = start; i < content.length; i++) {
    const c = content[i]
    if (inStr) {
      if (c === '\\') i++
      else if (c === '"') inStr = false
      continue
    }
    if (c === '"') inStr = true
    else if (c === '{') depth++
    else if (c === '}' && --depth === 0) return i
  }
  return -1
}

// Blocks carry their parsed value: this splitter runs on every render (up to
// 60fps while tokens stream, across every bubble in the transcript), and
// parsing each block twice — once to detect it, once to render it — was pure
// waste that got heavier when charts started carrying real series.
type ContentPart =
  | { type: 'md'; text: string }
  | { type: 'table'; raw: string; data: TableData | null }
  | { type: 'chart'; raw: string; data: ChartData | null }
  | { type: 'findings'; raw: string; data: FindingsData | null }
  | { type: 'pending' }

// Split assistant content into prose segments and netwatch-table blocks, so the
// structured tables render as real React (shaped severity icons) while the
// surrounding markdown still streams through Streamdown.
//
// Tolerant by design — the model is asked for a ```netwatch-table fence, but we
// also render the table if it arrives in any code fence or as a bare JSON object
// whose shape ({columns, rows}) matches. Streaming-safe: an unterminated table
// (open fence, or a `{` that hasn't closed) shows a placeholder, never raw JSON.
function splitAssistantContent(content: string): ContentPart[] {
  if (!content) return []
  const parts: ContentPart[] = []
  let textStart = 0
  let i = 0
  const flush = (end: number): void => {
    if (end > textStart) {
      const text = content.slice(textStart, end)
      if (text.trim()) parts.push({ type: 'md', text })
    }
  }

  while (i < content.length) {
    // A fenced block: ```[lang]\n ... ```
    if (content.startsWith('```', i)) {
      const lineEnd = content.indexOf('\n', i)
      const lang = content.slice(i + 3, lineEnd === -1 ? content.length : lineEnd).trim()
      const bodyStart = lineEnd === -1 ? content.length : lineEnd + 1
      const close = content.indexOf('```', bodyStart)
      const isTableFence = /netwatch[-_]?table/i.test(lang)
      const isChartFence = /netwatch[-_]?chart/i.test(lang)
      const isFindingsFence = /netwatch[-_]?findings/i.test(lang)
      if (close === -1) {
        // Unterminated. If it's one of our fences (or holds a partial block),
        // show a placeholder; otherwise let Streamdown render the partial block.
        if (isTableFence || isChartFence || isFindingsFence || parseNetwatchTable(content.slice(bodyStart))) {
          flush(i); parts.push({ type: 'pending' }); return parts
        }
        break
      }
      const body = content.slice(bodyStart, close)
      if (isChartFence) {
        flush(i)
        parts.push({ type: 'chart', raw: body, data: parseNetwatchChart(body) })
        i = close + 3; textStart = i; continue
      }
      if (isFindingsFence) {
        flush(i)
        parts.push({ type: 'findings', raw: body, data: parseFindingsBlock(body) })
        i = close + 3; textStart = i; continue
      }
      const data = parseNetwatchTable(body)
      if (isTableFence || data) {
        flush(i)
        parts.push({ type: 'table', raw: body, data })
        i = close + 3; textStart = i; continue
      }
      i = close + 3; continue   // unrelated code block — keep as text
    }

    // A bare JSON object that happens to be a table (no fence emitted).
    if (content[i] === '{') {
      const end = matchBrace(content, i)
      if (end === -1) {
        // Could be a table still streaming in — peek at what we have so far.
        if (parseNetwatchTable(content.slice(i)) || /"columns"\s*:/.test(content.slice(i, i + 200))) {
          flush(i); parts.push({ type: 'pending' }); return parts
        }
      } else {
        const body = content.slice(i, end + 1)
        const bare = parseNetwatchTable(body)
        if (bare) {
          flush(i)
          parts.push({ type: 'table', raw: body, data: bare })
          i = end + 1; textStart = i; continue
        }
      }
    }
    i++
  }
  flush(content.length)
  return parts
}

interface MdImageProps {
  src?: string
  alt?: string
  [key: string]: unknown
}

function MdImage({ src, alt, ...rest }: MdImageProps) {
  const [errored, setErrored] = useState(false)
  if (!src || errored) {
    return (
      <span className="nw-img-fallback" role="img" aria-label={alt || 'image'}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="3" width="18" height="18" rx="2" />
          <circle cx="8.5" cy="8.5" r="1.5" />
          <polyline points="21 15 16 10 5 21" />
        </svg>
        <span>{alt || 'Image unavailable'}</span>
      </span>
    )
  }
  return <img src={src} alt={alt || ''} onError={() => setErrored(true)} {...rest} />
}

// Inline severity token, e.g. `sev:5` or `sev:ok` — a drop-in replacement for
// colour-only emoji dots in prose. Renders the shaped icon inline. The prefix
// form also catches a token the model glued to a value in one code span
// (`sev:3 95% used`, 2026-07-28 screenshot): icon + the remainder as plain
// text, so the operator sees the number, never a literal "sev:3" chip.
const SEV_PREFIX = /^sev:(ok|[0-5])\b\s*/i

interface MdCodeProps {
  className?: string
  children?: ReactNode
  [key: string]: unknown
}

function MdCode(props: MdCodeProps) {
  const { className, children } = props
  const text = (Array.isArray(children) ? children.join('') : String(children ?? '')).trim()
  if (!className) {
    const m = SEV_PREFIX.exec(text)
    if (m) {
      const lvl = m[1].toLowerCase()
      const rest = text.slice(m[0].length)
      const icon = <SeverityIcon level={lvl === 'ok' ? 'ok' : Number(lvl)} size={14} />
      return rest ? <>{icon} {rest}</> : icon
    }
  }
  return <code {...props} />
}

const EMOJI_SEV_RE = /🔴|🟥|🟠|🟧|🟡|🟨|🟢|🟩|🔵|🟦|⚪|⬜|🟣|🟪/g
// Bare `sev:N` tokens not already wrapped in backticks (e.g. straight from a
// tool's cpu_status string). The lookbehind/lookahead skip already-backticked
// ones the model wrote in prose.
const BARE_TOKEN_RE = /(?<!`)\bsev:(ok|[0-5])\b(?!`)/gi

// Rewrite colour-only emoji dots AND bare `sev:N` tokens into `sev:N`
// inline-code so they render as shaped icons via MdCode — the model isn't
// reliable at emitting backticked tokens, so this is the safety net.
function preprocessSeverity(text: string): string {
  if (!text) return text
  // Skip fenced code so we don't rewrite tokens inside code samples.
  return text
    .split(/(```[\s\S]*?```)/g)
    .map((seg, i) =>
      i % 2
        ? seg
        : seg
            .replace(EMOJI_SEV_RE, (e) => `\`sev:${EMOJI_SEV[e]}\``)
            .replace(BARE_TOKEN_RE, (m) => `\`${m}\``))
    .join('')
}

const MD_COMPONENTS = {
  a: (props: Record<string, unknown>) => <a {...props} target="_blank" rel="noopener noreferrer" />,
  table: (props: Record<string, unknown>) => <table {...props} />,
  thead: (props: Record<string, unknown>) => <thead {...props} />,
  tbody: (props: Record<string, unknown>) => <tbody {...props} />,
  tr: (props: Record<string, unknown>) => <tr {...props} />,
  th: (props: Record<string, unknown>) => <th {...props} />,
  td: (props: Record<string, unknown>) => <td {...props} />,
  pre: (props: Record<string, unknown>) => <pre {...props} />,
  code: MdCode,
  inlineCode: MdCode,
  img: MdImage,
} as ComponentProps<typeof Streamdown>['components']

export interface AssistantContentProps {
  content: string
  /** Class applied to each markdown (Streamdown) segment, so the host keeps its
   *  own prose typography (the dashboard passes "md"; the widget relies on its
   *  `.nw-bubble` ancestor styles and omits it). */
  markdownClassName?: string
  /** Chart series by ref, for blocks the backend hasn't expanded inline.
   *  Normally empty: the answer arrives with its points already in it. */
  charts?: Record<string, ChartPayload>
  /** The turn is still streaming, so an unresolved chart ref is expected. */
  busy?: boolean
  /** Early findings cards by ref (`findings_card` SSE events): a ref-only
   *  fence renders from here while the stream is still ahead of hydration.
   *  A hydrated fence in the text always wins. */
  findingsCards?: Record<string, FindingsData>
}

/** The shared assistant-answer renderer: markdown + netwatch-tables/charts/findings + severity icons. */
export function AssistantContent({ content, markdownClassName, charts, busy, findingsCards }: AssistantContentProps) {
  // While the turn streams, a table that restates a card's rows is removed
  // before it paints — it is exactly what ensure_producer_cards will remove
  // from the final frame, so showing it building for seconds and then
  // vanishing was pure churn. The final text is authoritative: once the turn
  // is done the content renders as sent.
  const shown = useMemo(
    () => (busy ? dropDuplicateTables(content, findingsCards) : content),
    [content, busy, findingsCards],
  )
  const parts = useMemo(() => splitAssistantContent(shown), [shown])
  // While the turn streams, a producer card that arrived as an event but is
  // not yet referenced by the text paints at the top of the bubble — the
  // server guard guarantees the final text carries its fence, so this is the
  // final layout early, not a guess. Once the text references the ref (the
  // pasted/prepended fence), the fence render takes over and this is empty.
  const early = busy ? orphanFindingsCards(content, findingsCards) : []
  return (
    <>
      {early.map(({ ref, block }) => (
        <FindingsBlock key={`early-${ref}`} data={block} busy />
      ))}
      {parts.map((p, pi) =>
        p.type === 'table' ? (
          <NetwatchTable key={pi} data={p.data} raw={p.raw} />
        ) : p.type === 'chart' ? (
          <NetwatchChart
            key={pi}
            data={p.data}
            raw={p.raw}
            payload={p.data?.ref ? charts?.[p.data.ref] : undefined}
            busy={busy}
          />
        ) : p.type === 'findings' ? (
          <FindingsBlock key={pi} data={resolveFindingsRef(p.data, findingsCards)} raw={p.raw} busy={busy} />
        ) : p.type === 'pending' ? (
          <div key={pi} className="nw-ntable-pending">Building…</div>
        ) : (
          <Streamdown
            key={pi}
            className={markdownClassName}
            parseIncompleteMarkdown
            controls={false}
            components={MD_COMPONENTS}
          >
            {preprocessSeverity(p.text)}
          </Streamdown>
        ),
      )}
    </>
  )
}
